import { Center, chakra, Flex, Grid, GridItem, Image, Text, useBoolean } from "@chakra-ui/react";
import { GridProps } from "@chakra-ui/styled-system";
import classNames from "classnames";
import React, { useMemo, useState } from "react";
import { genBlock } from "@/utils/bem";
import { WithClassName } from "@/types/base";

import { IAlbumItem } from "@/modules/album/store";
import Icon from "@/components/Icon";
import { ReactComponent as EmptyAlbum } from "@/assets/icons/empty_album.svg";

import "./style.scss";
import OperationMenu from "@/modules/album/components/OperationMenu";
import { albumOpList } from "@/modules/album/constant";
import DeleteModal from "@/modules/album/components/DeleteModal";
import RenameModal from "@/modules/album/components/RenameModal";
import { compressedUrl } from "@/utils/string";
import { useTranslate } from "@/utils/lang";
import { observer } from "mobx-react-lite";
import { AlbumWords } from "@/utils/lang/album";

const block = genBlock("c-creator-album-item");
const AlbumItemComp: React.FC<
  WithClassName<{
    data: IAlbumItem;
    onClick: () => void;
  }>
> = ({ data, className, onClick }) => {
  const { translate } = useTranslate();
  const previewCount = data.preview.length;
  const previewGrid = useMemo(() => {
    if (previewCount <= 1) {
      const props: GridProps = {
        gridTemplateColumns: "180px",
        gridTemplateRows: "120px",
      };
      if (previewCount === 1) {
        props.gridTemplateAreas = `"image_0"`;
      }
      return props;
    }
    if (previewCount === 2) {
      return {
        gridTemplateColumns: "120px 1fr",
        gridTemplateRows: "120px",
        gridTemplateAreas: `
        "image_0 image_0 image_1"
        `,
      };
    }
    // 多张预览图
    const props: GridProps = {
      gridTemplateColumns: "120px 1fr",
      gridTemplateRows: "60px 60px",
      gridTemplateAreas: `
        "image_0 image_0 image_1"
        "image_0 image_0 image_2"
        `,
    };
    return props;
  }, [previewCount]);

  const [modalOpen, modalOp] = useBoolean(false);
  const [modalType, setModalType] = useState<"rename" | "delete">("rename");
  const onMenuSelect = (type: string) => {
    setModalType(type as "rename" | "delete");
    modalOp.on();
  };

  const modal = useMemo(() => {
    switch (modalType) {
      case "delete":
        return <DeleteModal isOpen={modalOpen} onClose={modalOp.off} albumId={data.id} />;
      case "rename":
        return <RenameModal isOpen={modalOpen} onClose={modalOp.off} albumId={data.id} />;
    }
  }, [modalOpen, data.id, modalOp]);

  return (
    <Flex
      key={data.id}
      className={classNames(block(), className)}
      w="100%"
      rounded="6px"
      alignItems="center"
      overflow="hidden"
      pos="relative"
      _hover={{
        bg: "#404040",
        cursor: "pointer",
      }}
      onClick={onClick}
    >
      <Grid
        {...previewGrid}
        mr="16px"
        rounded="6px"
        border="1px solid"
        borderColor="text.primary"
        overflow="hidden"
      >
        {data.preview?.length ? (
          data.preview.slice(0, 3).map((src, index) => (
            <GridItem key={index} gridArea={`image_${index}`}>
              <Image
                className={block(`preview-${index}`)}
                w="100%"
                h="100%"
                objectFit="cover"
                src={compressedUrl(src, 320)}
              />
            </GridItem>
          ))
        ) : (
          <GridItem>
            <Center w="100%" h="100%">
              <Icon fontSize="48px" svg={EmptyAlbum} />
            </Center>
          </GridItem>
        )}
      </Grid>
      <Flex color="text.subtitle" flexDir="column" justifyContent="center" fontWeight={500}>
        <Text w="92px" fontSize="md" mb="10px" className={block("name")}>
          {data.name}
        </Text>
        <Text fontSize="xs">
          {data.image_count} {translate(AlbumWords.pictureUnit)}
        </Text>
      </Flex>
      {/* action */}
      {data.is_default === 1 ? null : (
        <OperationMenu
          menuList={albumOpList}
          onSelect={onMenuSelect}
          placement="bottom-end"
          variant="creatorAlbumOp"
          styles={{
            buttonIcon: {
              fontSize: "20px",
              color: "text.secondary",
            },
          }}
        />
      )}
      {modal}
    </Flex>
  );
};

export const AlbumItem = chakra(observer(AlbumItemComp));
